import { memo, useCallback, useRef } from 'react';
import { Arrow, cn, Gallery } from '@divlab/divanui';

import useMedias from '@Hooks/useMedias';
import Img from '@UI/Img';
import Link from '@Navigation/Link';
import logo from './icons/logo.svg';
import styles from './InstagramPost.module.css';

import type { FC, HTMLAttributes } from 'react';
import type { IGalleryRef } from '@divlab/divanui';

type InstagramPostProps = InstagramPostData & HTMLAttributes<HTMLDivElement>;

export interface InstagramPostData {
  header: {
    name: string;
    link: string;
  };
  author: string;
  recordText: string;
  images: string[];
}

const InstagramPost: FC<InstagramPostProps> = (props) => {
  const { className, header, author, recordText, images, ...restProps } = props;
  const { isMobileM } = useMedias();
  const galleryRef = useRef<IGalleryRef>(null);

  const handlePrev = useCallback(() => galleryRef.current?.slidePrev(), []);
  const handleNext = useCallback(() => galleryRef.current?.slideNext(), []);

  if (!images.length) return null;

  return (
    <div className={cn(styles.wrapper, className)} {...restProps}>
      <div className={styles.container}>
        <div className={styles.header}>
          <Link className={styles.logoWrapper} to={header.link} target='_blank'>
            <Img className={styles.logo} src={logo} />
          </Link>
          <Link className={styles.account} to={header.link} target='_blank'>
            {header.name}
          </Link>
        </div>

        <div className={styles.galleryWrapper}>
          <Gallery ref={galleryRef} pagination slidesPerView={1}>
            {images.map((image) => (
              <Img className={styles.imageWrapper} cnImage={styles.image} key={image} src={image} />
            ))}
          </Gallery>

          {images.length > 1 && !isMobileM && (
            <>
              <Arrow position='left' className={styles.arrowPrev} onClick={handlePrev} />
              <Arrow position='right' className={styles.arrowNext} onClick={handleNext} />
            </>
          )}
        </div>

        <div className={styles.textWrapper}>
          <div className={styles.text}>
            {author && <span className={styles.author}>{author}</span>} {recordText}
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(InstagramPost);
